import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { api } from '../lib/api';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useAuthStore } from '../stores/authStore';
import { alert } from '../utils/toast';
import { LoadingState } from '../components/ui/LoadingState';
import { EmptyState } from '../components/ui/EmptyState';
import { Alert } from '../components/ui/Alert';
import { Pagination } from '../components/ui/Pagination';
import { SubscriptionCheckout } from '../components/SubscriptionCheckout';
import {
  CheckCircle2,
  PauseCircle,
  AlertTriangle,
  Search,
  BookOpen,
  FileCheck,
  Calculator,
} from 'lucide-react';
import { AppCredentialsManager } from '../components/AppCredentialsManager';
import { SubscriptionReceiptModal } from '../components/SubscriptionReceiptModal';
import { PricingQuotaSimulator } from '../components/PricingQuotaSimulator';
import type { Subscription, Product, Plan, Application } from '../types';

interface ProductWithPlans extends Product {
  plans: Plan[];
  authentication_provider?: 'BASIC_AUTH' | 'OAUTH2' | 'API_KEY' | 'HMAC' | 'CUSTOM';
}

interface ContextType {
  application: Application;
}

export const Subscriptions: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { environment } = useEnvironment();
  const client = useAuthStore((state) => state.client);
  const [searchParams] = useSearchParams();
  const { application } = useOutletContext<ContextType>();

  // Check if we're in the "new subscription" flow
  const isNewSubscription = window.location.pathname.endsWith('/subscriptions/new');
  const productId = searchParams.get('product');
  const planId = searchParams.get('plan');

  // Filters and Pagination
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [receiptSub, setReceiptSub] = useState<Subscription | null>(null);
  const [simulatingProduct, setSimulatingProduct] = useState<{
    name: string;
    plans: Plan[];
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
      setOffset(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setOffset(0);
  }, [filters.status, environment]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'subscriptions',
      environment,
      client?.id,
      application.id,
      filters.status,
      debouncedSearch,
      offset,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (debouncedSearch) params.append('search', debouncedSearch);
      params.append('limit', limit.toString());
      params.append('offset', offset.toString());

      const response = await api.get<{
        success: boolean;
        data: Subscription[];
        meta: { total: number };
      }>(`/clients/${client?.id}/subscriptions?${params.toString()}`);

      const filteredData = response.data.data.filter(
        (sub) => sub.application_id === application.id
      );

      return { data: filteredData, meta: response.data.meta };
    },
    enabled: !!client?.id && !!application.id && !isNewSubscription,
  });

  const subscriptions = data?.data;
  const total = data?.meta?.total || 0;

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const { data: products } = useQuery({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ data: ProductWithPlans[] }>('/products')).data.data,
    enabled: isNewSubscription,
  });

  const changePlanMutation = useMutation({
    mutationFn: async (data: { subscription_id: string; plan_id: string }) => {
      return (
        await api.put(`/subscriptions/${data.subscription_id}/plan`, { plan_id: data.plan_id })
      ).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
  });

  const cancelMutation = useMutation({
    mutationFn: async (id: string) => await api.delete(`/subscriptions/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscriptions'] }),
  });

  const handleCancel = async (id: string, productName: string) => {
    const result = await alert.confirm(
      'Cancelar Assinatura?',
      `Tem a certeza que deseja cancelar a assinatura de "${productName}"? O acesso será mantido até ao final do ciclo de faturação atual.`
    );
    if (result.isConfirmed) cancelMutation.mutate(id);
  };

  const handleChangePlan = async (
    subscriptionId: string,
    newPlanId: string,
    productName: string
  ) => {
    const result = await alert.confirm(
      'Alterar Plano?',
      `Mudar o plano para "${productName}"? As melhorias aplicam-se imediatamente, as reduções no próximo ciclo de faturação.`
    );
    if (result.isConfirmed)
      changePlanMutation.mutate({ subscription_id: subscriptionId, plan_id: newPlanId });
  };

  // ── New subscription flow ───────────────────────────────────────────────
  if (isNewSubscription && productId && planId) {
    const product = products?.find((p) => p.id === productId);

    return (
      <div className="animate-fade-in">
        <div className="mb-8 bg-white dark:bg-slate-900 p-8 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Subscrever {product?.name}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Aplica-se à aplicação <strong>{application.name}</strong> (
            {environment === 'sandbox' ? 'Sandbox' : 'Produção'})
          </p>
        </div>

        {product ? (
          <SubscriptionCheckout
            product={product}
            planId={planId}
            applications={[application]} // Pass the specific application to restrict checkout choices
            environment={environment}
            onSuccess={() => {
              navigate(`/applications/${application.id}/subscriptions`);
              queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
            }}
          />
        ) : (
          <Alert variant="error">Produto não encontrado.</Alert>
        )}
      </div>
    );
  }

  // ── Subscriptions list ──────────────────────────────────────────────────
  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Pesquisar por produto..."
            className="w-full pl-10 pr-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-slate-900 dark:text-white transition-colors"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <div className="w-full sm:w-48">
          <select
            className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary-500 outline-none text-slate-900 dark:text-white transition-colors"
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
          >
            <option value="">Todos os Estados</option>
            <option value="active">Ativas</option>
            <option value="cancelled">Canceladas</option>
            <option value="expired">Expiradas</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <LoadingState type="card" count={3} />
      ) : subscriptions && subscriptions.length > 0 ? (
        <>
          <div className="grid gap-6">
            {subscriptions.map((subscription) => {
              const usagePercentage = subscription.usage
                ? (subscription.usage.current_usage / (subscription.plan?.monthly_quota || 1)) * 100
                : 0;

              return (
                <div
                  key={subscription.id}
                  className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                        {subscription.product?.name || 'Produto Desconhecido'}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                            subscription.status === 'active'
                              ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                              : subscription.status === 'cancelled'
                                ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400'
                                : 'bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400'
                          }`}
                        >
                          {subscription.status === 'active' ? (
                            <>
                              <CheckCircle2 className="w-4 h-4" /> Ativa
                            </>
                          ) : subscription.status === 'cancelled' ? (
                            <>
                              <PauseCircle className="w-4 h-4" /> Cancelada
                            </>
                          ) : (
                            <>
                              <AlertTriangle className="w-4 h-4" /> Inativa
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          const matchedProduct = products?.find(
                            (p) => p.id === subscription.product_id
                          );
                          const plans =
                            matchedProduct?.plans || (subscription.plan ? [subscription.plan] : []);
                          setSimulatingProduct({
                            name: subscription.product?.name || matchedProduct?.name || 'API',
                            plans,
                          });
                        }}
                        className="text-indigo-600 dark:text-indigo-400 font-semibold text-sm hover:underline flex items-center gap-1"
                        title="Simular volume de tráfego e estimativa de quotas"
                      >
                        <Calculator className="w-4 h-4" /> Simular Quotas
                      </button>
                      <button
                        onClick={() => setReceiptSub(subscription)}
                        className="text-emerald-600 dark:text-emerald-400 font-semibold text-sm hover:underline flex items-center gap-1"
                        title="Ver / Imprimir Recibo Oficial"
                      >
                        <FileCheck className="w-4 h-4" /> Recibo
                      </button>
                      <button
                        onClick={() =>
                          navigate(
                            `/applications/${application.id}/products/${subscription.product_id}`
                          )
                        }
                        className="text-primary-600 dark:text-primary-400 font-semibold text-sm hover:underline flex items-center gap-1"
                      >
                        <BookOpen className="w-4 h-4" /> Ver Produto
                      </button>
                    </div>
                  </div>

                  {subscription.plan && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 mb-6">
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">
                          Plano
                        </p>
                        <p className="text-base font-semibold text-slate-900 dark:text-white capitalize">
                          {subscription.plan.tier}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">
                          Cota Mensal
                        </p>
                        <p className="text-base font-semibold text-slate-900 dark:text-white">
                          {subscription.plan.monthly_quota.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">
                          Limite/min
                        </p>
                        <p className="text-base font-semibold text-slate-900 dark:text-white">
                          {subscription.plan.rate_limit_per_minute}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold tracking-wider mb-1">
                          Preço
                        </p>
                        <p className="text-base font-semibold text-slate-900 dark:text-white">
                          ${subscription.plan.monthly_price}/mês
                        </p>
                      </div>
                    </div>
                  )}

                  {subscription.usage && (
                    <div className="mb-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                          Consumo Mensal da Quota
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono font-bold text-slate-900 dark:text-white">
                            {subscription.usage.current_usage.toLocaleString()} /{' '}
                            {subscription.plan?.monthly_quota.toLocaleString()} reqs
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              usagePercentage > 85
                                ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400'
                                : usagePercentage > 60
                                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-400'
                                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                            }`}
                          >
                            {usagePercentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="w-full rounded-full h-2.5 overflow-hidden bg-slate-200 dark:bg-slate-700/80">
                        <div
                          className={`h-2.5 rounded-full transition-all duration-500 ${
                            usagePercentage > 85
                              ? 'bg-gradient-to-r from-amber-500 to-rose-500'
                              : usagePercentage > 60
                                ? 'bg-gradient-to-r from-primary-500 to-amber-500'
                                : 'bg-gradient-to-r from-emerald-500 to-primary-500'
                          }`}
                          style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                        />
                      </div>
                      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 mt-2">
                        <span>
                          {subscription.usage.remaining_quota.toLocaleString()} requisições disponíveis
                        </span>
                        {usagePercentage > 85 && (
                          <span className="text-rose-600 dark:text-rose-400 font-semibold text-[11px]">
                            Quota quase esgotada. Considere fazer upgrade.
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-6 text-sm text-slate-600 dark:text-slate-400 mb-6 pb-6 border-b border-slate-100 dark:border-slate-800">
                    <div>
                      <span className="font-semibold text-slate-900 dark:text-slate-300">
                        Início:
                      </span>{' '}
                      {new Date(subscription.start_date).toLocaleDateString()}
                    </div>
                    {subscription.end_date && (
                      <div>
                        <span className="font-semibold text-slate-900 dark:text-slate-300">
                          Término:
                        </span>{' '}
                        {new Date(subscription.end_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {subscription.status === 'active' && subscription.product?.plans && (
                    <div className="mb-4">
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">
                        Mudar de Plano:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {subscription.product.plans
                          .filter((plan: Plan) => plan.id !== subscription.plan_id)
                          .map((plan: Plan) => (
                            <button
                              key={plan.id}
                              onClick={() =>
                                handleChangePlan(
                                  subscription.id,
                                  plan.id,
                                  subscription.product?.name || ''
                                )
                              }
                              disabled={changePlanMutation.isPending}
                              className="bg-slate-100 hover:bg-slate-200 text-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-200 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                            >
                              Mudar para {plan.tier}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  {subscription.status === 'active' && (
                    <div className="mb-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <AppCredentialsManager
                        applicationId={application.id}
                        clientId={client?.id || ''}
                        productId={subscription.product_id}
                        authProvider={subscription.product?.authentication_provider || 'CUSTOM'}
                      />
                    </div>
                  )}

                  {subscription.status === 'active' && (
                    <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                      <button
                        onClick={() =>
                          handleCancel(subscription.id, subscription.product?.name || '')
                        }
                        disabled={cancelMutation.isPending}
                        className="text-error-600 hover:text-error-700 hover:bg-error-50 dark:hover:bg-error-900/20 px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                      >
                        Cancelar Assinatura
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            <Pagination
              total={total}
              limit={limit}
              offset={offset}
              onPageChange={setOffset}
              onLimitChange={setLimit}
            />
          </div>
        </>
      ) : (
        <EmptyState
          title={`Nenhuma assinatura para esta aplicação`}
          description={`A aplicação ${application.name} não tem assinaturas ativas. Explore o catálogo para começar.`}
          action={
            <button
              onClick={() => navigate(`/applications/${application.id}/catalog`)}
              className="btn-base btn-md btn-primary px-6 py-3 shadow-md"
            >
              <BookOpen className="w-5 h-5 mr-2 inline" /> Explorar Catálogo
            </button>
          }
        />
      )}

      <SubscriptionReceiptModal
        isOpen={!!receiptSub}
        onClose={() => setReceiptSub(null)}
        subscription={receiptSub}
        clientName={client?.company_name}
        clientEmail={client?.email}
      />

      {simulatingProduct && (
        <PricingQuotaSimulator
          productName={simulatingProduct.name}
          plans={simulatingProduct.plans}
          isModal={true}
          isOpen={true}
          onClose={() => setSimulatingProduct(null)}
          onSelectPlan={(planId) => {
            const prod = products?.find((p) => p.name === simulatingProduct.name);
            setSimulatingProduct(null);
            navigate(
              `/applications/${application.id}/subscriptions/new?${prod ? `product=${prod.id}&` : ''}plan=${planId}`
            );
          }}
        />
      )}
    </div>
  );
};
